import {
  Box,
  Card,
  Flex,
  Heading,
  Icon,
  Progress,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { MdCheckCircle } from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import { usePostHog } from 'posthog-js/react';
import { spacing, typography, useSemanticColor } from 'theme/tokens';
import { translate } from '../../../i18n';
import { CHECKLIST_ITEMS } from '../constants';
import { useChecklistItems } from '../hooks/useChecklistItems';
import { useWalkthroughStore } from '../hooks/useWalkthroughStore';
import ChecklistItem from './ChecklistItem';

type Props = {
  uid: string;
};

export default function WalkthroughChecklist({ uid }: Props) {
  const { items, completedCount, totalCount, allComplete } = useChecklistItems(uid);
  const { dismissed } = useWalkthroughStore(uid);
  const posthog = usePostHog();

  const textColor = useSemanticColor('textPrimary');
  const mutedColor = useSemanticColor('textSecondary');
  const accentBg = useColorModeValue('brand.500', 'brand.400');
  const progressBg = useColorModeValue('blue.50', 'whiteAlpha.100');

  // Fire completion event exactly once
  const hasReportedCompletion = useRef(false);
  useEffect(() => {
    if (allComplete && !dismissed && !hasReportedCompletion.current) {
      posthog?.capture('walkthrough_checklist_completed');
      hasReportedCompletion.current = true;
    }
  }, [allComplete, dismissed, posthog]);

  const handleItemNavigate = (itemId: string) => {
    posthog?.capture('walkthrough_checklist_item_clicked', { itemId, completedCount, totalCount });
  };

  // All complete — celebration card
  if (allComplete && !dismissed) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
      >
        <Card
          bg={accentBg}
          borderRadius="16px"
          p={spacing.cardHeader}
          role="status"
          aria-live="polite"
        >
          <Flex align="center" gap={3}>
            <Icon as={MdCheckCircle} w="32px" h="32px" color="white" />
            <Box>
              <Heading as="h3" color="white" {...typography.sectionTitle}>
                {translate('walkthrough.all_complete_title')}
              </Heading>
              <Text color="whiteAlpha.800" {...typography.body} mt={1}>
                {translate('walkthrough.all_complete_description')}
              </Text>
            </Box>
          </Flex>
        </Card>
      </motion.div>
    );
  }

  // Dismissed — show restore link (rendered in HomePage greeting zone)
  if (dismissed) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.3 }}
      >
        <Card borderRadius="16px" pt={spacing.card} w="100%" overflow="hidden">
          {/* Header */}
          <Flex px={spacing.cardHeader} mb={4} justify="space-between" align="center">
            <Box>
              <Heading as="h2" color={textColor} {...typography.sectionTitle}>
                {translate('walkthrough.checklist_title')}
              </Heading>
              <Text color={mutedColor} {...typography.caption} mt={1}>
                {completedCount} / {totalCount} {translate('walkthrough.complete')}
              </Text>
            </Box>
          </Flex>

          {/* Progress bar */}
          <Box px={spacing.cardHeader} mb={4}>
            <Progress
              value={(completedCount / totalCount) * 100}
              size="sm"
              borderRadius="full"
              colorScheme="brand"
              bg={progressBg}
              sx={{ '& > div': { transition: 'width 0.4s ease' } }}
              aria-label="Setup progress"
              aria-valuenow={completedCount}
              aria-valuemax={totalCount}
            />
          </Box>

          {/* Items */}
          <Box as="ul" role="list" pb={2}>
            {items.map((item) => {
              const def = CHECKLIST_ITEMS.find((d) => d.id === item.id)!;
              return (
                <ChecklistItem
                  key={item.id}
                  id={item.id}
                  icon={def.icon}
                  route={def.route}
                  completed={item.completed}
                  loading={item.loading}
                  unknown={item.unknown}
                  onNavigate={() => handleItemNavigate(item.id)}
                />
              );
            })}
          </Box>

        </Card>
      </motion.div>
    </AnimatePresence>
  );
}
